import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { HttpStatusCode } from 'axios';
import { DashboardInceptedApeServiceV2 } from '../../dashboard-services/dashboard-incepted-ape/dashboard-incepted-ape-v2.service';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { InceptedApeSummaryV2 } from '../../dashboard-interfaces/incepted-ape-summary-v2.interface';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { InceptedApeByAdvisorsV2 } from '../../dashboard-interfaces/incepted-ape-by-advisors-v2.interface';
import { InceptedApeByBranchesV2 } from '../../dashboard-interfaces/incepted-ape-by-branches-v2.interface';
import { ApeCarrierSplitV2 } from '../../dashboard-interfaces/ape-carrier-split-v2.interface';
import { ApeCarrierSplitServiceV2 } from '../../dashboard-services/ape-carrier-split/ape-carrier-split-v2.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ViewInceptedApeBranchAdvisorsDialogV2 } from '../../dashboard-dialogs/view-incepted-ape-branch-advisors/view-incepted-ape-branch-advisors-v2.dialog';
import { DashboardManagerTeamServiceV2 } from '../../dashboard-services/dashboard-manager-team/dashboard-manager-team-v2.service';
import { ViewInceptedApeManagerAdvisorsDialogV2 } from '../../dashboard-dialogs/view-incepted-ape-manager-advisors/view-incepted-ape-manager-advisors-v2.dialog';
import { Router } from '@angular/router';
import { TabStateV2 } from '../../dashboard-interfaces/tab-state-v2.interface';
import { SubmissionApeByAdvisorsV2 } from '../../dashboard-interfaces/submission-ape-by-advisors-v2.interface';
import { SubmissionApeByBranchesV2 } from '../../dashboard-interfaces/submission-ape-by-branches-v2.interface';
import { ViewSubmissionApeBranchAdvisorsDialogV2 } from '../../dashboard-dialogs/view-submission-ape-branch-advisors/view-submission-ape-branch-advisors-v2.dialog';
import { ViewSubmissionApeManagerAdvisorsDialogV2 } from '../../dashboard-dialogs/view-submission-ape-manager-advisors/view-submission-ape-manager-advisors-v2.dialog';
import { ApeSegmentV2, InceptedApeCardComponentV2 } from './incepted-ape-card/incepted-ape-card-v2.component';
import { InceptedApeDrawerComponentV2 } from './incepted-ape-drawer/incepted-ape-drawer-v2.component';
import { ApeStatCardComponentV2 } from './ape-stat-card/ape-stat-card-v2.component';

@Component({
  selector: 'dashboard-ape-v2',
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatDividerModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    CurrencyPipe,
    ReactiveFormsModule,
    MatIconModule,
    InceptedApeCardComponentV2,
    InceptedApeDrawerComponentV2,
    ApeStatCardComponentV2
  ],
  templateUrl: './ape-v2.component.html',
  styleUrl: './ape-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApeComponentV2 implements OnInit, AfterViewInit {

  private router = inject(Router);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  private dashboardInceptedApeService = inject(DashboardInceptedApeServiceV2);
  private dashboardManagerTeamService = inject(DashboardManagerTeamServiceV2);
  // Carrier colours, monograms and the per-row MTD/YTD split — shared with the
  // by-manager / by-branch advisor dialogs so every table splits them the same way.
  private readonly apeCarrierSplitService = inject(ApeCarrierSplitServiceV2);
  private readonly matDialog = inject(MatDialog);

  protected currentUserRole: string = '';

  protected totalMonthInceptedApe: number = 0;
  protected totalYearInceptedApe: number = 0;

  // Per-company breakdown feeding the redesigned Incepted APE cards.
  // Built from the aggregated summary today ([Singlife, Other Companies]);
  // renders N companies once the backend supplies a per-company array.
  protected inceptedApeMtdSegments: ApeSegmentV2[] = [];
  protected inceptedApeYtdSegments: ApeSegmentV2[] = [];

  /** Which Incepted APE card's full-width carrier drawer is open. */
  protected expandedIncepted: 'mtd' | 'ytd' | null = null;

  protected toggleIncepted(which: 'mtd' | 'ytd'): void {
    this.expandedIncepted = this.expandedIncepted === which ? null : which;
  }

  // ── In-cell carrier chips (design 1c) ─────────────────────────────
  // The Incepted APE tables show one MTD and one YTD cell holding the combined
  // total across every carrier; expanding a row swaps in a segmented mix bar and
  // a chip per carrier. Expansion is tracked per table, keyed by the row's own
  // identifier, so paging and sorting keep the open rows open.
  private readonly expandedInceptedManagerRows = new Set<string>();
  private readonly expandedInceptedAdvisorRows = new Set<string>();
  private readonly expandedInceptedBranchRows = new Set<string>();
  private readonly expandedIndividualInceptedBranchRows = new Set<string>();

  protected isInceptedManagerRowExpanded(repCode: string): boolean {
    return this.expandedInceptedManagerRows.has(repCode);
  }

  protected toggleInceptedManagerRow(repCode: string): void {
    this.toggleRow(this.expandedInceptedManagerRows, repCode);
  }

  protected isInceptedAdvisorRowExpanded(repCode: string): boolean {
    return this.expandedInceptedAdvisorRows.has(repCode);
  }

  protected toggleInceptedAdvisorRow(repCode: string): void {
    this.toggleRow(this.expandedInceptedAdvisorRows, repCode);
  }

  protected isInceptedBranchRowExpanded(branchName: string): boolean {
    return this.expandedInceptedBranchRows.has(branchName);
  }

  protected toggleInceptedBranchRow(branchName: string): void {
    this.toggleRow(this.expandedInceptedBranchRows, branchName);
  }

  protected isIndividualInceptedBranchRowExpanded(repCode: string): boolean {
    return this.expandedIndividualInceptedBranchRows.has(repCode);
  }

  protected toggleIndividualInceptedBranchRow(repCode: string): void {
    this.toggleRow(this.expandedIndividualInceptedBranchRows, repCode);
  }

  private toggleRow(open: Set<string>, key: string): void {
    if (open.has(key)) {
      open.delete(key);
    } else {
      open.add(key);
    }
  }

  protected inceptedApeSummary: InceptedApeSummaryV2 = {
    monthSinglife: 0,
    monthNonsinglife: 0,
    monthSinglifeCase: 0,
    monthNonsinglifeCase: 0,
    yearSinglife: 0,
    yearNonsinglife: 0,
    yearSinglifeCase: 0,
    yearNonsinglifeCase: 0,
    yearTarget: 0,
    monthChinalife: 0,
    monthChinalifeCase: 0,
    yearChinalife: 0,
    yearChinalifeCase: 0,
    monthChinataiping: 0,
    monthChinataipingCase: 0,
    yearChinataiping: 0,
    yearChinataipingCase: 0,
    monthEtiqa: 0,
    monthEtiqaCase: 0,
    yearEtiqa: 0,
    yearEtiqaCase: 0,
    monthFwd: 0,
    monthFwdCase: 0,
    yearFwd: 0,
    yearFwdCase: 0,
    monthIncome: 0,
    monthIncomeCase: 0,
    yearIncome: 0,
    yearIncomeCase: 0,
    monthManulife: 0,
    monthManulifeCase: 0,
    yearManulife: 0,
    yearManulifeCase: 0,
    monthRhi: 0,
    monthRhiCase: 0,
    yearRhi: 0,
    yearRhiCase: 0,
    monthTokiomarine: 0,
    monthTokiomarineCase: 0,
    yearTokiomarine: 0,
    yearTokiomarineCase: 0,
    monthTransamerica: 0,
    monthTransamericaCase: 0,
    yearTransamerica: 0,
    yearTransamericaCase: 0,
    monthIfast: 0,
    monthIfastCase: 0,
    yearIfast: 0,
    yearIfastCase: 0,
    monthGrow: 0,
    monthGrowCase: 0,
    yearGrow: 0,
    yearGrowCase: 0,
    monthTiger: 0,
    monthTigerCase: 0,
    yearTiger: 0,
    yearTigerCase: 0,
    monthFwdEmber: 0,
    monthFwdEmberCase: 0,
    yearFwdEmber: 0,
    yearFwdEmberCase: 0,
    monthHsbc: 0,
    monthHsbcCase: 0,
    yearHsbc: 0,
    yearHsbcCase: 0,
    monthSubmissionSinglife: 0,
    monthSubmissionSinglifeCase: 0,
    yearSubmissionSinglife: 0,
    yearSubmissionSinglifeCase: 0,
    yearPendingSinglife: 0,
    yearPendingSinglifeCase: 0,
  };

  private inceptedApeByAdvisorData: InceptedApeByAdvisorsV2[] = [];
  protected inceptedApeByAdvisorTableDisplayDataRow: string[] = [];
  protected inceptedApeByAdvisorTableDataSource = new MatTableDataSource<InceptedApeByAdvisorsV2>();

  private submissionApeByAdvisorData: SubmissionApeByAdvisorsV2[] = [];
  protected submissionApeByAdvisorTableDisplayDataRow: string[] = [];
  protected submissionApeByAdvisorTableDisplayFirstColumn: string[] = [];
  protected submissionApeByAdvisorTableDisplaySecondColumn: string[] = ['monthSubmissionSinglife', 'monthSubmissionSinglifeCase', 'yearSubmissionSinglife', 'yearSubmissionSinglifeCase', 'yearPendingSinglife', 'yearPendingSinglifeCase'];
  protected submissionApeByAdvisorTableDataSource = new MatTableDataSource<SubmissionApeByAdvisorsV2>();

  private inceptedApeByBranchData: InceptedApeByBranchesV2[] = [];
  // Carrier-chip layout (design 1c): one MTD and one YTD cell holding the
  // combined total, expandable into a mix bar + a chip per carrier.
  protected inceptedApeByBranchTableDisplayColumn: string[] = ["branchName", "mtdByCarrier", "ytdByCarrier"];
  protected inceptedApeByBranchTableDataSource = new MatTableDataSource<InceptedApeByBranchesV2>();

  private submissionApeByBranchData: SubmissionApeByBranchesV2[] = [];
  protected submissionApeByBranchTableDisplayColumn: string[] = ["branchName", "monthSubmissionSinglife", "monthSubmissionSinglifeCase", "yearSubmissionSinglife", "yearSubmissionSinglifeCase", "yearPendingSinglife", "yearPendingSinglifeCase"];
  protected submissionApeByBranchTableDataSource = new MatTableDataSource<SubmissionApeByBranchesV2>();

  private inceptedApeByManagerData: InceptedApeByAdvisorsV2[] = [];
  protected inceptedApeByManagerTableDisplayDataRow: string[] = ["repCode", "personName", "mtdByCarrier", "ytdByCarrier"];
  protected inceptedApeByManagerTableDataSource = new MatTableDataSource<InceptedApeByAdvisorsV2>();

  private submissionApeByManagerData: SubmissionApeByAdvisorsV2[] = [];
  protected submissionApeByManagerTableDisplayDataRow: string[] = ["repCode", "personName", "monthSubmissionSinglife", "monthSubmissionSinglifeCase", "yearSubmissionSinglife", "yearSubmissionSinglifeCase", "yearPendingSinglife", "yearPendingSinglifeCase"];
  protected submissionApeByManagerTableDisplayFirstColumn: string[] = ['repCode', 'personName', 'header-row2-month-singlife-group', 'header-row2-year-singlife-group', 'header-row2-year-pendinglife-group'];
  protected submissionApeByManagerTableDisplaySecondColumn: string[] = ['monthSubmissionSinglife', 'monthSubmissionSinglifeCase', 'yearSubmissionSinglife', 'yearSubmissionSinglifeCase', 'yearPendingSinglife', 'yearPendingSinglifeCase'];
  protected submissionApeByManagerTableDataSource = new MatTableDataSource<SubmissionApeByAdvisorsV2>();

  // Individual (rep-level) incepted APE for the branch. Reuses
  // InceptedApeByAdvisorsV2 — same row shape as the by-team table — so the MTD
  // cell can render the shared per-carrier breakdown. MTD only, by request.
  private individualInceptedApeByBranchData: InceptedApeByAdvisorsV2[] = [];
  protected individualInceptedApeByBranchTableDisplayDataRow: string[] = ["repCode", "personName", "branch", "ytdByCarrier"];
  protected individualInceptedApeByBranchTableDataSource = new MatTableDataSource<InceptedApeByAdvisorsV2>();

  inceptedApeByAdvisorTableFilterFc = new FormControl("");
  inceptedApeByBranchTableFilterFc = new FormControl("");
  inceptedApeByManagerTableFilterFc = new FormControl("");
  submissionApeByAdvisorTableFilterFc = new FormControl("");
  submissionApeByManagerTableFilterFc = new FormControl("");
  submissionApeByBranchTableFilterFc = new FormControl("");
  individualInceptedApeByBranchTableFilterFc = new FormControl("");

  setSprinner = signal(false);
  isInceptedApeByAdvisorTableHidden = signal(false);
  isInceptedApeByBranchTableHidden = signal(false);
  isInceptedApeByManagerTableHidden = signal(false);
  isSubmissionApeByAdvisorTableHidden = signal(false);
  isSubmissionApeByBranchTableHidden = signal(false);
  isSubmissionApeByManagerTableHidden = signal(false);
  isindividualInceptedApeByBranchTableHidden = signal(false);

  @ViewChild('inceptedApeByAdvisorTablePaginator')
  inceptedApeByAdvisorTablePaginator!: MatPaginator;
  @ViewChild('inceptedApeByAdvisorTableSort')
  inceptedApeByAdvisorTableSort = new MatSort();
  @ViewChild('inceptedApeByBranchTablePaginator')
  inceptedApeByBranchTablePaginator!: MatPaginator;
  @ViewChild('inceptedApeByBranchTableSort')
  inceptedApeByBranchTableSort = new MatSort();
  @ViewChild('inceptedApeByManagerTablePaginator')
  inceptedApeByManagerTablePaginator!: MatPaginator;
  @ViewChild('inceptedApeByManagerTableSort')
  inceptedApeByManagerTableSort = new MatSort();

  @ViewChild('submissionApeByAdvisorTablePaginator')
  submissionApeByAdvisorTablePaginator!: MatPaginator;
  @ViewChild('submissionApeByAdvisorTableSort')
  submissionApeByAdvisorTableSort = new MatSort();
  @ViewChild('submissionApeByManagerTablePaginator')
  submissionApeByManagerTablePaginator!: MatPaginator;
  @ViewChild('submissionApeByManagerTableSort')
  submissionApeByManagerTableSort = new MatSort();
  @ViewChild('submissionApeByBranchTablePaginator')
  submissionApeByBranchTablePaginator !: MatPaginator;
  @ViewChild('submissionApeByBranchTableSort')
  submissionApeByBranchTableSort = new MatSort();

  @ViewChild('individualInceptedApeByBranchTablePaginator')
  individualInceptedApeByBranchTablePaginator!: MatPaginator;
  @ViewChild('individualInceptedApeByBranchTableSort')
  individualInceptedApeByBranchTableSort = new MatSort();

  @Input('tabState')
  set tabState(state: TabStateV2) {
    this.isCurrentTabActive = state.isActive;
    this.targetYear = state.targetYear;

    this.setAsCurrentTab();
  }

  protected inceptedApeMtdDisclaimer: string = "";
  protected inceptedApeYtdDisclaimer: string = "";

  protected isCurrentTabActive: boolean = false;
  protected targetYear: string = "";

  constructor() { }

  async ngOnInit(): Promise<void> {
    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();

    if (['Corporate', 'BDM'].includes(this.currentUserRole)) {
      this.inceptedApeByAdvisorTableDisplayDataRow = ["repCode", "personName", "branch", "mtdByCarrier", "ytdByCarrier"/*, "yearTotal"*/];
      this.submissionApeByAdvisorTableDisplayDataRow = ["repCode", "personName", "branch", "monthSubmissionSinglife", "monthSubmissionSinglifeCase", "yearSubmissionSinglife", "yearSubmissionSinglifeCase", "yearPendingSinglife", "yearPendingSinglifeCase"];
      this.submissionApeByAdvisorTableDisplayFirstColumn = ['repCode', 'personName', 'branch', 'header-row2-month-singlife-group', 'header-row2-year-singlife-group', 'header-row2-year-pendinglife-group'];
    }
    else {
      this.inceptedApeByAdvisorTableDisplayDataRow = ["repCode", "personName", "mtdByCarrier", "ytdByCarrier"/*, "yearTotal"*/];
      this.submissionApeByAdvisorTableDisplayDataRow = ["repCode", "personName", "monthSubmissionSinglife", "monthSubmissionSinglifeCase", "yearSubmissionSinglife", "yearSubmissionSinglifeCase", "yearPendingSinglife", "yearPendingSinglifeCase"];
      this.submissionApeByAdvisorTableDisplayFirstColumn = ['repCode', 'personName', 'header-row2-month-singlife-group', 'header-row2-year-singlife-group', 'header-row2-year-pendinglife-group'];
    }
  }

  ngAfterViewInit(): void {
    this.inceptedApeByAdvisorTableDataSource.paginator = this.inceptedApeByAdvisorTablePaginator;
    this.inceptedApeByAdvisorTableDataSource.sort = this.inceptedApeByAdvisorTableSort;

    this.inceptedApeByBranchTableDataSource.paginator = this.inceptedApeByBranchTablePaginator;
    this.inceptedApeByBranchTableDataSource.sort = this.inceptedApeByBranchTableSort;

    this.inceptedApeByManagerTableDataSource.paginator = this.inceptedApeByManagerTablePaginator;
    this.inceptedApeByManagerTableDataSource.sort = this.inceptedApeByManagerTableSort;

    this.submissionApeByAdvisorTableDataSource.paginator = this.submissionApeByAdvisorTablePaginator;
    this.submissionApeByAdvisorTableDataSource.sort = this.submissionApeByAdvisorTableSort;

    this.submissionApeByBranchTableDataSource.paginator = this.submissionApeByBranchTablePaginator;
    this.submissionApeByBranchTableDataSource.sort = this.submissionApeByBranchTableSort;

    this.submissionApeByManagerTableDataSource.paginator = this.submissionApeByManagerTablePaginator;
    this.submissionApeByManagerTableDataSource.sort = this.submissionApeByManagerTableSort;

    this.individualInceptedApeByBranchTableDataSource.paginator = this.individualInceptedApeByBranchTablePaginator;
    this.individualInceptedApeByBranchTableDataSource.sort = this.individualInceptedApeByBranchTableSort;
  }

  async setAsCurrentTab(): Promise<void> {
    this.inceptedApeByAdvisorData = [];
    this.inceptedApeByBranchData = [];
    this.inceptedApeByManagerData = [];
    this.submissionApeByBranchData = [];
    this.submissionApeByAdvisorData = [];
    this.submissionApeByManagerData = [];
    this.individualInceptedApeByBranchData = [];

    if (this.isCurrentTabActive) {
      await this.getDashboardInceptedApe();

      this.totalMonthInceptedApe = this.inceptedApeSummary.monthSinglife + this.inceptedApeSummary.monthNonsinglife;
      this.totalYearInceptedApe = this.inceptedApeSummary.yearSinglife + this.inceptedApeSummary.yearNonsinglife;

      if (this.inceptedApeByAdvisorData.length > 0) {
        // this.inceptedApeByAdvisorTableDataSource.data = this.inceptedApeByAdvisorData;
        this.__applyInceptedApeByAdvisorDataFilter();
        this.__applySubmissionApeByAdvisorDataFilter();
        this.isInceptedApeByAdvisorTableHidden.set(false);
        this.isSubmissionApeByAdvisorTableHidden.set(false);
      } else {
        this.isInceptedApeByAdvisorTableHidden.set(true);
        this.isSubmissionApeByAdvisorTableHidden.set(true);
      }

      if (this.inceptedApeByBranchData.length > 0) {
        // this.inceptedApeByBranchTableDataSource.data = this.inceptedApeByBranchData;
        this.__applyInceptedApeByBranchDataFilter();
        this.__applySubmissionApeByBranchDataFilter();
        this.isInceptedApeByBranchTableHidden.set(false);
        this.isSubmissionApeByBranchTableHidden.set(false);
      } else {
        this.isInceptedApeByBranchTableHidden.set(true);
        this.isSubmissionApeByBranchTableHidden.set(true);
      }

      if (['Director'].includes(this.currentUserRole)) {
        await this.getDashboardDirectorwiseManagers();
        // this.inceptedApeByManagerTableDataSource.data = this.inceptedApeByManagerData;
        this.__applyInceptedApeByManagerDataFilter();
        this.__applySubmissionApeByManagerDataFilter();
      } else {
        this.isInceptedApeByManagerTableHidden.set(true);
        this.isSubmissionApeByManagerTableHidden.set(true);
      }

      if (['Corporate', 'BDM'].includes(this.currentUserRole)) {
        this.isindividualInceptedApeByBranchTableHidden.set(true);
      } else {
        await this.getDashboardIndividualInceptedApeByBranch();
        this.__applyIndividualInceptedApeByBranchDataFilter();
      }
    }
  }

  async getDashboardInceptedApe() {
    this.setSprinner.set(true);

    const responseData = await this.dashboardInceptedApeService
      .getDashboardInceptedApe(Number.parseInt(this.targetYear))
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      // Manager, Director : apedata, apesummary
      // Agent : apesummary
      // Corporate : branchdata, corporateapedata
      // BDM : bdmapedata, bdmbranchreporting
      if (['Corporate', 'BDM'].includes(this.currentUserRole)) {
        this.mapApeSummaryData(responseData.data.responsedata.corporateapedata[0], responseData.data.responsedata.projectconfiguration);
        this.__formatInceptedApeByBranchData(responseData.data.responsedata.branchdata, responseData.data.responsedata.affinitybranch);
        this.__formatSubmissionApeByBranchData(responseData.data.responsedata.branchdata, responseData.data.responsedata.affinitybranch);
      }
      // else if (this.currentUserRole === 'BDM') {
      //   this.mapApeSummaryData(responseData.data.responsedata.bdmapedata[0], responseData.data.responsedata.projectconfiguration);
      //   this.__formatInceptedApeByBranchData(responseData.data.responsedata.bdmbranchreporting, responseData.data.responsedata.affinitybranch);
      //   this.__formatSubmissionApeByBranchData(responseData.data.responsedata.bdmbranchreporting, responseData.data.responsedata.affinitybranch);
      // }
      else if (['Manager', 'Director'].includes(this.currentUserRole)) {
        this.__formatInceptedApeByAdvisorData(responseData.data.responsedata.apedata);
        this.__formatSubmissionApeByAdvisorData(responseData.data.responsedata.apedata);
        this.mapApeSummaryData(responseData.data.responsedata.apesummary[0], responseData.data.responsedata.projectconfiguration);
      }
      else {
        this.mapApeSummaryData(responseData.data.responsedata.apesummary[0], responseData.data.responsedata.projectconfiguration);
      }

      this.setDisclaimers(responseData.data.responsedata.disclamirdata);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async getDashboardDirectorwiseManagers() {
    this.setSprinner.set(true);

    const responseData = await this.dashboardManagerTeamService
      .getDashboardInceptedApeDirectorwiseManager(Number.parseInt(this.targetYear))
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      let managerTableData: any[] = responseData.data.responsedata;
      managerTableData.forEach((manager) => {
        this.inceptedApeByManagerData.push({
          repCode: manager.piasRepCode.toString(),
          personName: manager.agentRealName.toString(),
          branch: manager.branch.toString(),
          monthSinglife: Number.parseInt(manager.mtdSinglife.toString()),
          monthSinglifeCase: Number.parseInt(manager.mtdSinglifeCase.toString()),
          monthNonsinglife: Number.parseInt(manager.mtdNonSinglife.toString()),
          monthNonsinglifeCase: Number.parseInt(manager.mtdNonSinglifeCase.toString()),
          yearSinglife: Number.parseInt(manager.ytdSinglife.toString()),
          yearSinglifeCase: Number.parseInt(manager.ytdSinglifeCase.toString()),
          yearNonsinglife: Number.parseInt(manager.ytdNonSinglife.toString()),
          yearNonsinglifeCase: Number.parseInt(manager.ytdNonSinglifeCase.toString()),
          monthChinalife: Number.parseInt(manager.mtdChinalife.toString()),
          monthChinalifeCase: Number.parseInt(manager.mtdChinalifeCase.toString()),
          yearChinalife: Number.parseInt(manager.ytdChinalife.toString()),
          yearChinalifeCase: Number.parseInt(manager.ytdChinalifeCase.toString()),
          monthChinataiping: Number.parseInt(manager.mtdChinataiping.toString()),
          monthChinataipingCase: Number.parseInt(manager.mtdChinataipingCase.toString()),
          yearChinataiping: Number.parseInt(manager.ytdChinataiping.toString()),
          yearChinataipingCase: Number.parseInt(manager.ytdChinataipingCase.toString()),
          monthEtiqa: Number.parseInt(manager.mtdEtiqa.toString()),
          monthEtiqaCase: Number.parseInt(manager.mtdEtiqaCase.toString()),
          yearEtiqa: Number.parseInt(manager.ytdEtiqa.toString()),
          yearEtiqaCase: Number.parseInt(manager.ytdEtiqaCase.toString()),
          monthFwd: Number.parseInt(manager.mtdFwd.toString()),
          monthFwdCase: Number.parseInt(manager.mtdFwdCase.toString()),
          yearFwd: Number.parseInt(manager.ytdFwd.toString()),
          yearFwdCase: Number.parseInt(manager.ytdFwdCase.toString()),
          monthIncome: Number.parseInt(manager.mtdIncome.toString()),
          monthIncomeCase: Number.parseInt(manager.mtdIncomeCase.toString()),
          yearIncome: Number.parseInt(manager.ytdIncome.toString()),
          yearIncomeCase: Number.parseInt(manager.ytdIncomeCase.toString()),
          monthManulife: Number.parseInt(manager.mtdManulife.toString()),
          monthManulifeCase: Number.parseInt(manager.mtdManulifeCase.toString()),
          yearManulife: Number.parseInt(manager.ytdManulife.toString()),
          yearManulifeCase: Number.parseInt(manager.ytdManulifeCase.toString()),
          monthRhi: Number.parseInt(manager.mtdRhi.toString()),
          monthRhiCase: Number.parseInt(manager.mtdRhiCase.toString()),
          yearRhi: Number.parseInt(manager.ytdRhi.toString()),
          yearRhiCase: Number.parseInt(manager.ytdRhiCase.toString()),
          monthTokiomarine: Number.parseInt(manager.mtdTokiomarine.toString()),
          monthTokiomarineCase: Number.parseInt(manager.mtdTokiomarineCase.toString()),
          yearTokiomarine: Number.parseInt(manager.ytdTokiomarine.toString()),
          yearTokiomarineCase: Number.parseInt(manager.ytdTokiomarineCase.toString()),
          monthTransamerica: Number.parseInt(manager.mtdTransamerica.toString()),
          monthTransamericaCase: Number.parseInt(manager.mtdTransamericaCase.toString()),
          yearTransamerica: Number.parseInt(manager.ytdTransamerica.toString()),
          yearTransamericaCase: Number.parseInt(manager.ytdTransamericaCase.toString()),
          monthIfast: Number.parseInt(manager.mtdIfast.toString()),
          monthIfastCase: Number.parseInt(manager.mtdIfastCase.toString()),
          yearIfast: Number.parseInt(manager.ytdIfast.toString()),
          yearIfastCase: Number.parseInt(manager.ytdIfastCase.toString()),
          monthGrow: Number.parseInt(manager.mtdGrow.toString()),
          monthGrowCase: Number.parseInt(manager.mtdGrowCase.toString()),
          yearGrow: Number.parseInt(manager.ytdGrow.toString()),
          yearGrowCase: Number.parseInt(manager.ytdGrowCase.toString()),
          monthTiger: Number.parseInt(manager.mtdTiger.toString()),
          monthTigerCase: Number.parseInt(manager.mtdTigerCase.toString()),
          yearTiger: Number.parseInt(manager.ytdTiger.toString()),
          yearTigerCase: Number.parseInt(manager.ytdTigerCase.toString()),
          monthFwdEmber: Number.parseInt(manager.mtdFwdember.toString()),
          monthFwdEmberCase: Number.parseInt(manager.mtdFwdemberCase.toString()),
          yearFwdEmber: Number.parseInt(manager.ytdFwdember.toString()),
          yearFwdEmberCase: Number.parseInt(manager.ytdFwdemberCase.toString()),
          monthHsbc: Number.parseInt(manager.mtdHsbc.toString()),
          monthHsbcCase: Number.parseInt(manager.mtdHsbcCase.toString()),
          yearHsbc: Number.parseInt(manager.ytdHsbc.toString()),
          yearHsbcCase: Number.parseInt(manager.ytdHsbcCase.toString()),
          yearTarget: 0,
          yearTotal: Number.parseInt(manager.ytdSinglife.toString()) + Number.parseInt(manager.ytdNonSinglife.toString()),
          userGroup: manager.userGroup.toString()
        });
        this.submissionApeByManagerData.push({
          repCode: manager.piasRepCode.toString(),
          personName: manager.agentRealName.toString(),
          branch: manager.branch.toString(),
          monthSubmissionSinglife: Number.parseInt(manager.mtdSubmissionSinglife.toString()),
          monthSubmissionSinglifeCase: Number.parseInt(manager.mtdSubmissionSinglifeCase.toString()),
          yearSubmissionSinglife: Number.parseInt(manager.ytdSubmissionSinglife.toString()),
          yearSubmissionSinglifeCase: Number.parseInt(manager.ytdSubmissionSinglifeCase.toString()),
          yearPendingSinglife: Number.parseInt(manager.ytdPendingSinglife.toString()),
          yearPendingSinglifeCase: Number.parseInt(manager.ytdPendingSinglifeCase.toString()),
          yearTarget: 0,
          userGroup: manager.userGroup.toString()
        });
      });

      this.apeCarrierSplitService.applyCarrierSplit(this.inceptedApeByManagerData);

      // this.inceptedApeByManagerData = this.inceptedApeByManagerData.filter(item => item.userGroup !== 'Affinity');
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async getDashboardIndividualInceptedApeByBranch(): Promise<void> {
    this.setSprinner.set(true);

    const responseData = await this.dashboardInceptedApeService
      .getDashboardIndividualInceptedApeByBranch(Number.parseInt(this.targetYear))
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      let branchTableData: any[] = responseData.data.responsedata;
      this.__formatIndividualInceptedApeByBranchData(branchTableData);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  /**
   * ⚠️ API shape assumed to match `dashboardNewApeDirectorwiseManagers` — the MTD
   * cell renders the shared per-carrier breakdown, which needs exactly those
   * `mtd<Carrier>` / `mtd<Carrier>Case` fields. Cross-check the names against the
   * Go backend once the endpoint is wired up.
   */
  private __formatIndividualInceptedApeByBranchData(individualApeDataList: any[]) {
    individualApeDataList.forEach((apeItem: any) => {
      this.individualInceptedApeByBranchData.push({
        repCode: apeItem.piasRepCode.toString(),
        personName: apeItem.agentRealName.toString(),
        branch: apeItem.branch.toString(),
        monthSinglife: Number.parseInt((apeItem.mtdSinglife.toString())),
        monthSinglifeCase: Number.parseInt((apeItem.mtdSinglifeCase.toString())),
        monthNonsinglife: Number.parseInt((apeItem.mtdNonSinglife.toString())),
        monthNonsinglifeCase: Number.parseInt((apeItem.mtdNonSinglifeCase.toString())),
        yearSinglife: Number.parseInt((apeItem.ytdSinglife.toString())),
        yearSinglifeCase: Number.parseInt((apeItem.ytdSinglifeCase.toString())),
        yearNonsinglife: Number.parseInt((apeItem.ytdNonSinglife.toString())),
        yearNonsinglifeCase: Number.parseInt((apeItem.ytdNonSinglifeCase.toString())),
        monthChinalife: Number.parseInt((apeItem.mtdChinalife.toString())),
        monthChinalifeCase: Number.parseInt((apeItem.mtdChinalifeCase.toString())),
        yearChinalife: Number.parseInt((apeItem.ytdChinalife.toString())),
        yearChinalifeCase: Number.parseInt((apeItem.ytdChinalifeCase.toString())),
        monthChinataiping: Number.parseInt((apeItem.mtdChinataiping.toString())),
        monthChinataipingCase: Number.parseInt((apeItem.mtdChinataipingCase.toString())),
        yearChinataiping: Number.parseInt((apeItem.ytdChinataiping.toString())),
        yearChinataipingCase: Number.parseInt((apeItem.ytdChinataipingCase.toString())),
        monthEtiqa: Number.parseInt((apeItem.mtdEtiqa.toString())),
        monthEtiqaCase: Number.parseInt((apeItem.mtdEtiqaCase.toString())),
        yearEtiqa: Number.parseInt((apeItem.ytdEtiqa.toString())),
        yearEtiqaCase: Number.parseInt((apeItem.ytdEtiqaCase.toString())),
        monthFwd: Number.parseInt((apeItem.mtdFwd.toString())),
        monthFwdCase: Number.parseInt((apeItem.mtdFwdCase.toString())),
        yearFwd: Number.parseInt((apeItem.ytdFwd.toString())),
        yearFwdCase: Number.parseInt((apeItem.ytdFwdCase.toString())),
        monthIncome: Number.parseInt((apeItem.mtdIncome.toString())),
        monthIncomeCase: Number.parseInt((apeItem.mtdIncomeCase.toString())),
        yearIncome: Number.parseInt((apeItem.ytdIncome.toString())),
        yearIncomeCase: Number.parseInt((apeItem.ytdIncomeCase.toString())),
        monthManulife: Number.parseInt((apeItem.mtdManulife.toString())),
        monthManulifeCase: Number.parseInt((apeItem.mtdManulifeCase.toString())),
        yearManulife: Number.parseInt((apeItem.ytdManulife.toString())),
        yearManulifeCase: Number.parseInt((apeItem.ytdManulifeCase.toString())),
        monthRhi: Number.parseInt((apeItem.mtdRhi.toString())),
        monthRhiCase: Number.parseInt((apeItem.mtdRhiCase.toString())),
        yearRhi: Number.parseInt((apeItem.ytdRhi.toString())),
        yearRhiCase: Number.parseInt((apeItem.ytdRhiCase.toString())),
        monthTokiomarine: Number.parseInt((apeItem.mtdTokiomarine.toString())),
        monthTokiomarineCase: Number.parseInt((apeItem.mtdTokiomarineCase.toString())),
        yearTokiomarine: Number.parseInt((apeItem.ytdTokiomarine.toString())),
        yearTokiomarineCase: Number.parseInt((apeItem.ytdTokiomarineCase.toString())),
        monthTransamerica: Number.parseInt((apeItem.mtdTransamerica.toString())),
        monthTransamericaCase: Number.parseInt((apeItem.mtdTransamericaCase.toString())),
        yearTransamerica: Number.parseInt((apeItem.ytdTransamerica.toString())),
        yearTransamericaCase: Number.parseInt((apeItem.ytdTransamericaCase.toString())),
        monthIfast: Number.parseInt((apeItem.mtdIfast.toString())),
        monthIfastCase: Number.parseInt((apeItem.mtdIfastCase.toString())),
        yearIfast: Number.parseInt((apeItem.ytdIfast.toString())),
        yearIfastCase: Number.parseInt((apeItem.ytdIfastCase.toString())),
        monthGrow: Number.parseInt((apeItem.mtdGrow.toString())),
        monthGrowCase: Number.parseInt((apeItem.mtdGrowCase.toString())),
        yearGrow: Number.parseInt((apeItem.ytdGrow.toString())),
        yearGrowCase: Number.parseInt((apeItem.ytdGrowCase.toString())),
        monthTiger: Number.parseInt((apeItem.mtdTiger.toString())),
        monthTigerCase: Number.parseInt((apeItem.mtdTigerCase.toString())),
        yearTiger: Number.parseInt((apeItem.ytdTiger.toString())),
        yearTigerCase: Number.parseInt((apeItem.ytdTigerCase.toString())),
        monthFwdEmber: Number.parseInt(apeItem.mtdFwdember.toString()),
        monthFwdEmberCase: Number.parseInt(apeItem.mtdFwdemberCase.toString()),
        yearFwdEmber: Number.parseInt(apeItem.ytdFwdember.toString()),
        yearFwdEmberCase: Number.parseInt(apeItem.ytdFwdemberCase.toString()),
        monthHsbc: Number.parseInt(apeItem.mtdHsbc.toString()),
        monthHsbcCase: Number.parseInt(apeItem.mtdHsbcCase.toString()),
        yearHsbc: Number.parseInt(apeItem.ytdHsbc.toString()),
        yearHsbcCase: Number.parseInt(apeItem.ytdHsbcCase.toString()),
        yearTarget: 0,
        yearTotal: 0,
        userGroup: apeItem.userGroup.toString()
      });
    });

    // Fills monthTotal / monthTotalCase and the per-carrier chips the MTD cell renders.
    this.apeCarrierSplitService.applyCarrierSplit(this.individualInceptedApeByBranchData);
  }

  clearIndividualInceptedApeByBranchSearch() {
    this.individualInceptedApeByBranchTableFilterFc.setValue('');
    this.__applyIndividualInceptedApeByBranchDataFilter();
  }

  clearInceptedApeByAdvisorSearch() {
    this.inceptedApeByAdvisorTableFilterFc.setValue('');
    this.__applyInceptedApeByAdvisorDataFilter();
  }

  clearSubmissionApeByAdvisorSearch() {
    this.submissionApeByAdvisorTableFilterFc.setValue('');
    this.__applySubmissionApeByAdvisorDataFilter();
  }

  clearInceptedBranchSearch() {
    this.inceptedApeByBranchTableFilterFc.setValue('');
    this.__applyInceptedApeByBranchDataFilter();
  }
  clearSubmissionBranchSearch() {
    this.submissionApeByBranchTableFilterFc.setValue('');
    this.__applySubmissionApeByBranchDataFilter();
  }

  clearInceptedManagerSearch() {
    this.inceptedApeByManagerTableFilterFc.setValue('');
    this.__applyInceptedApeByManagerDataFilter();
  }

  clearSubmissionManagerSearch() {
    this.submissionApeByManagerTableFilterFc.setValue('');
    this.__applySubmissionApeByManagerDataFilter();
  }

  private setDisclaimers(disclaimer: any[]) {
    if (this.targetYear == new Date().getFullYear().toString()) {
      this.inceptedApeMtdDisclaimer = disclaimer.filter(dmItem => dmItem.dmType === "incepted_ape_mtd")[0].dmText.toString();
      this.inceptedApeYtdDisclaimer = disclaimer.filter(dmItem => dmItem.dmType === "incepted_ape_ytd")[0].dmText.toString();

    } else {
      this.inceptedApeMtdDisclaimer = "The Information is based on data as at 31/12/2025 for Singlife and 31/12/2025 for Non-Singife.";
      this.inceptedApeYtdDisclaimer = "The Information is based on data as at 31/12/2025 for Singlife and 31/12/2025 for Non-Singife.";
    }
  }

  private mapApeSummaryData(apeData: any, projConfig: any[]) {
    this.inceptedApeSummary = {
      monthSinglife: Number.parseInt(apeData.mtdSinglife),
      monthNonsinglife: Number.parseInt(apeData.mtdNonSinglife),
      monthSinglifeCase: Number.parseInt(apeData.mtdSinglifeCase),
      monthNonsinglifeCase: Number.parseInt(apeData.mtdNonSinglifeCase),
      yearSinglife: Number.parseInt(apeData.ytdSinglife),
      yearNonsinglife: Number.parseInt(apeData.ytdNonSinglife),
      yearSinglifeCase: Number.parseInt(apeData.ytdSinglifeCase),
      yearNonsinglifeCase: Number.parseInt(apeData.ytdNonSinglifeCase),
      yearTarget: Number.parseInt(projConfig.filter(pcItem => pcItem.pjConfName === "Incepted_APE_Year_Target")[0].pjConfValue),
      monthChinalife: Number.parseInt(apeData.mtdChinalife),
      monthChinalifeCase: Number.parseInt(apeData.mtdChinalifeCase),
      yearChinalife: Number.parseInt(apeData.ytdChinalife),
      yearChinalifeCase: Number.parseInt(apeData.ytdChinalifeCase),
      monthChinataiping: Number.parseInt(apeData.mtdChinataiping),
      monthChinataipingCase: Number.parseInt(apeData.mtdChinataipingCase),
      yearChinataiping: Number.parseInt(apeData.ytdChinataiping),
      yearChinataipingCase: Number.parseInt(apeData.ytdChinataipingCase),
      monthEtiqa: Number.parseInt(apeData.mtdEtiqa),
      monthEtiqaCase: Number.parseInt(apeData.mtdEtiqaCase),
      yearEtiqa: Number.parseInt(apeData.ytdEtiqa),
      yearEtiqaCase: Number.parseInt(apeData.ytdEtiqaCase),
      monthFwd: Number.parseInt(apeData.mtdFwd),
      monthFwdCase: Number.parseInt(apeData.mtdFwdCase),
      yearFwd: Number.parseInt(apeData.ytdFwd),
      yearFwdCase: Number.parseInt(apeData.ytdFwdCase),
      monthIncome: Number.parseInt(apeData.mtdIncome),
      monthIncomeCase: Number.parseInt(apeData.mtdIncomeCase),
      yearIncome: Number.parseInt(apeData.ytdIncome),
      yearIncomeCase: Number.parseInt(apeData.ytdIncomeCase),
      monthManulife: Number.parseInt(apeData.mtdManulife),
      monthManulifeCase: Number.parseInt(apeData.mtdManulifeCase),
      yearManulife: Number.parseInt(apeData.ytdManulife),
      yearManulifeCase: Number.parseInt(apeData.ytdManulifeCase),
      monthRhi: Number.parseInt(apeData.mtdRhi),
      monthRhiCase: Number.parseInt(apeData.mtdRhiCase),
      yearRhi: Number.parseInt(apeData.ytdRhi),
      yearRhiCase: Number.parseInt(apeData.ytdRhiCase),
      monthTokiomarine: Number.parseInt(apeData.mtdTokiomarine),
      monthTokiomarineCase: Number.parseInt(apeData.mtdTokiomarineCase),
      yearTokiomarine: Number.parseInt(apeData.ytdTokiomarine),
      yearTokiomarineCase: Number.parseInt(apeData.ytdTokiomarineCase),
      monthTransamerica: Number.parseInt(apeData.mtdTransamerica),
      monthTransamericaCase: Number.parseInt(apeData.mtdTransamericaCase),
      yearTransamerica: Number.parseInt(apeData.ytdTransamerica),
      yearTransamericaCase: Number.parseInt(apeData.ytdTransamericaCase),
      monthIfast: Number.parseInt(apeData.mtdIfast),
      monthIfastCase: Number.parseInt(apeData.mtdIfastCase),
      yearIfast: Number.parseInt(apeData.ytdIfast),
      yearIfastCase: Number.parseInt(apeData.ytdIfastCase),
      monthGrow: Number.parseInt(apeData.mtdGrow),
      monthGrowCase: Number.parseInt(apeData.mtdGrowCase),
      yearGrow: Number.parseInt(apeData.ytdGrow),
      yearGrowCase: Number.parseInt(apeData.ytdGrowCase),
      monthTiger: Number.parseInt(apeData.mtdTiger),
      monthTigerCase: Number.parseInt(apeData.mtdTigerCase),
      yearTiger: Number.parseInt(apeData.ytdTiger),
      yearTigerCase: Number.parseInt(apeData.ytdTigerCase),
      monthFwdEmber: Number.parseInt(apeData.mtdFwdember),
      monthFwdEmberCase: Number.parseInt(apeData.mtdFwdemberCase),
      yearFwdEmber: Number.parseInt(apeData.ytdFwdember),
      yearFwdEmberCase: Number.parseInt(apeData.ytdFwdemberCase),
      monthHsbc: Number.parseInt(apeData.mtdHsbc),
      monthHsbcCase: Number.parseInt(apeData.mtdHsbcCase),
      yearHsbc: Number.parseInt(apeData.ytdHsbc),
      yearHsbcCase: Number.parseInt(apeData.ytdHsbcCase),
      monthSubmissionSinglife: Number.parseInt(apeData.mtdSubmissionSinglife),
      monthSubmissionSinglifeCase: Number.parseInt(apeData.mtdSubmissionSinglifeCase),
      yearSubmissionSinglife: Number.parseInt(apeData.ytdSubmissionSinglife),
      yearSubmissionSinglifeCase: Number.parseInt(apeData.ytdSubmissionSinglifeCase),
      yearPendingSinglife: Number.parseInt(apeData.ytdPendingSinglife),
      yearPendingSinglifeCase: Number.parseInt(apeData.ytdPendingSinglifeCase)
    };

    // Build the per-company breakdown for the redesigned cards.
    // ⚠️ Backend contract: `apeData.mtdCompanies` / `apeData.ytdCompanies` are
    // expected to be arrays of { company, ape, cases } once the API provides a
    // per-company split. Until then we fall back to a single "Other Companies"
    // segment from the aggregated Non-Singlife total. Cross-check the exact
    // field names against the Go backend before relying on them.
    // const mtdCompanies = apeData.mtdCompanies ?? (this.apeCarrierSplitService.PREVIEW_COMPANY_BREAKDOWN
    //   ? this.apeCarrierSplitService.buildMockCompanies(this.inceptedApeSummary.monthNonsinglife, this.inceptedApeSummary.monthNonsinglifeCase)
    //   : undefined);
    // const ytdCompanies = apeData.ytdCompanies ?? (this.apeCarrierSplitService.PREVIEW_COMPANY_BREAKDOWN
    //   ? this.apeCarrierSplitService.buildMockCompanies(this.inceptedApeSummary.yearNonsinglife, this.inceptedApeSummary.yearNonsinglifeCase)
    //   : undefined);

    this.inceptedApeMtdSegments = this.buildApeSegments(
      this.inceptedApeSummary.monthSinglife,
      this.inceptedApeSummary.monthSinglifeCase,
      this.inceptedApeSummary.monthChinalife,
      this.inceptedApeSummary.monthChinalifeCase,
      this.inceptedApeSummary.monthChinataiping,
      this.inceptedApeSummary.monthChinataipingCase,
      this.inceptedApeSummary.monthEtiqa,
      this.inceptedApeSummary.monthEtiqaCase,
      this.inceptedApeSummary.monthFwd,
      this.inceptedApeSummary.monthFwdCase,
      this.inceptedApeSummary.monthGrow,
      this.inceptedApeSummary.monthGrowCase,
      this.inceptedApeSummary.monthIfast,
      this.inceptedApeSummary.monthIfastCase,
      this.inceptedApeSummary.monthIncome,
      this.inceptedApeSummary.monthIncomeCase,
      this.inceptedApeSummary.monthManulife,
      this.inceptedApeSummary.monthManulifeCase,
      this.inceptedApeSummary.monthRhi,
      this.inceptedApeSummary.monthRhiCase,
      this.inceptedApeSummary.monthTiger,
      this.inceptedApeSummary.monthTigerCase,
      this.inceptedApeSummary.monthTokiomarine,
      this.inceptedApeSummary.monthTokiomarineCase,
      this.inceptedApeSummary.monthTransamerica,
      this.inceptedApeSummary.monthTransamericaCase,
      this.inceptedApeSummary.monthFwdEmber,
      this.inceptedApeSummary.monthFwdEmberCase,
      this.inceptedApeSummary.monthHsbc,
      this.inceptedApeSummary.monthHsbcCase,


    );
    this.inceptedApeYtdSegments = this.buildApeSegments(
      this.inceptedApeSummary.yearSinglife,
      this.inceptedApeSummary.yearSinglifeCase,
      this.inceptedApeSummary.yearChinalife,
      this.inceptedApeSummary.yearChinalifeCase,
      this.inceptedApeSummary.yearChinataiping,
      this.inceptedApeSummary.yearChinataipingCase,
      this.inceptedApeSummary.yearEtiqa,
      this.inceptedApeSummary.yearEtiqaCase,
      this.inceptedApeSummary.yearFwd,
      this.inceptedApeSummary.yearFwdCase,
      this.inceptedApeSummary.yearGrow,
      this.inceptedApeSummary.yearGrowCase,
      this.inceptedApeSummary.yearIfast,
      this.inceptedApeSummary.yearIfastCase,
      this.inceptedApeSummary.yearIncome,
      this.inceptedApeSummary.yearIncomeCase,
      this.inceptedApeSummary.yearManulife,
      this.inceptedApeSummary.yearManulifeCase,
      this.inceptedApeSummary.yearRhi,
      this.inceptedApeSummary.yearRhiCase,
      this.inceptedApeSummary.yearTiger,
      this.inceptedApeSummary.yearTigerCase,
      this.inceptedApeSummary.yearTokiomarine,
      this.inceptedApeSummary.yearTokiomarineCase,
      this.inceptedApeSummary.yearTransamerica,
      this.inceptedApeSummary.yearTransamericaCase,
      this.inceptedApeSummary.yearFwdEmber,
      this.inceptedApeSummary.yearFwdEmberCase,
      this.inceptedApeSummary.yearHsbc,
      this.inceptedApeSummary.yearHsbcCase,
    );
  }

  /**
   * Assembles the Incepted APE segments: Singlife first, then either the real
   * per-company breakdown (when supplied) or a single aggregated
   * "Other Companies" segment (current API).
   */
  private buildApeSegments(
    singlife: number,
    singlifeCase: number,
    Chinalife: number,
    ChinalifeCase: number,
    Chinataiping: number,
    ChinataipingCase: number,
    Etiqa: number,
    EtiqaCase: number,
    Fwd: number,
    FwdCase: number,
    Grow: number,
    GrowCase: number,
    Ifast: number,
    IfastCase: number,
    Income: number,
    IncomeCase: number,
    Manulife: number,
    ManulifeCase: number,
    Rhi: number,
    RhiCase: number,
    Tiger: number,
    TigerCase: number,
    Tokiomarine: number,
    TokiomarineCase: number,
    Transamerica: number,
    TransamericaCase: number,
    Fwdember: number,
    FwdemberCase: number,
    Hsbc: number,
    HsbcCase: number,
  ): ApeSegmentV2[] {
    const segments: ApeSegmentV2[] = [
      {
        name: 'Singlife',
        ape: singlife,
        cases: singlifeCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('Singlife')
      },
      {
        name: 'China Life',
        ape: Chinalife,
        cases: ChinalifeCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('china life')
      },
      {
        name: 'China Taiping',
        ape: Chinataiping,
        cases: ChinataipingCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('china taiping')
      },
      {
        name: 'Etiqa',
        ape: Etiqa,
        cases: EtiqaCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('etiqa')
      },
      {
        name: 'FWD',
        ape: Fwd,
        cases: FwdCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('fwd')
      },
      {
        name: 'Grow',
        ape: Grow,
        cases: GrowCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('grow')
      },
      {
        name: 'iFAST',
        ape: Ifast,
        cases: IfastCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('ifast')
      },
      {
        name: 'Income',
        ape: Income,
        cases: IncomeCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('income')
      },
      {
        name: 'Manulife',
        ape: Manulife,
        cases: ManulifeCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('manulife')
      },
      {
        name: 'RHI',
        ape: Rhi,
        cases: RhiCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('rhi')
      },
      {
        name: 'TIGER',
        ape: Tiger,
        cases: TigerCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('tiger')
      },
      {
        name: 'Tokio Marine',
        ape: Tokiomarine,
        cases: TokiomarineCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('tokio marine')
      },
      {
        name: 'Transamerica',
        ape: Transamerica,
        cases: TransamericaCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('transamerica')
      },
      {
        name: 'FWD Ember',
        ape: Fwdember,
        cases: FwdemberCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('fwdember')
      },
      {
        name: 'HSBC',
        ape: Hsbc,
        cases: HsbcCase,
        logoUrl: this.apeCarrierSplitService.logoForCompany('hsbc')
      },
    ];

    // if (Array.isArray(companies) && companies.length > 0) {
    //   companies.forEach((item, index) => {
    //     segments.push({
    //       name: item.company,
    //       ape: Number(item.ape) || 0,
    //       cases: Number(item.cases) || 0,
    //       color: this.apeCarrierSplitService.colorForCompany(item.company, index + 1),
    //       initial: this.apeCarrierSplitService.initialOf(item.company),
    //       logoUrl: this.apeCarrierSplitService.logoForCompany(item.company)
    //     });
    //   });
    // } else {
    //   segments.push({
    //     name: 'Other Companies',
    //     ape: Tiger,
    //     cases: TigerCase,
    //     color: this.apeCarrierSplitService.colorForCompany('Other Companies', 1),
    //     initial: 'O'
    //   });
    // }

    return segments;
  }

  /** Carriers to render for a row, tolerant of rows the split never reached. */
  protected carriersOf(row: InceptedApeByAdvisorsV2 | InceptedApeByBranchesV2): ApeCarrierSplitV2[] {
    return this.apeCarrierSplitService.carriersOf(row);
  }
  private __formatInceptedApeByAdvisorData(inceptedApeByAdvisorDataList: any[]) {
    inceptedApeByAdvisorDataList.forEach((inceptedApeByAdvisorDataItem: any) => {
      this.inceptedApeByAdvisorData.push({
        repCode: inceptedApeByAdvisorDataItem.piasRepCode,
        personName: inceptedApeByAdvisorDataItem.agentRealName.toString().trim(),
        branch: inceptedApeByAdvisorDataItem.branch.toString().trim(),
        monthSinglife: inceptedApeByAdvisorDataItem.mtdSinglife,
        monthSinglifeCase: inceptedApeByAdvisorDataItem.mtdSinglifeCase,
        monthNonsinglife: inceptedApeByAdvisorDataItem.mtdNonSinglife,
        monthNonsinglifeCase: inceptedApeByAdvisorDataItem.mtdNonSinglifeCase,
        yearSinglife: inceptedApeByAdvisorDataItem.ytdSinglife,
        yearSinglifeCase: inceptedApeByAdvisorDataItem.ytdSinglifeCase,
        yearNonsinglife: inceptedApeByAdvisorDataItem.ytdNonSinglife,
        yearNonsinglifeCase: inceptedApeByAdvisorDataItem.ytdNonSinglifeCase,
        monthChinalife: inceptedApeByAdvisorDataItem.mtdChinalife,
        monthChinalifeCase: inceptedApeByAdvisorDataItem.mtdChinalifeCase,
        yearChinalife: inceptedApeByAdvisorDataItem.ytdChinalife,
        yearChinalifeCase: inceptedApeByAdvisorDataItem.ytdChinalifeCase,
        monthChinataiping: inceptedApeByAdvisorDataItem.mtdChinataiping,
        monthChinataipingCase: inceptedApeByAdvisorDataItem.mtdChinataipingCase,
        yearChinataiping: inceptedApeByAdvisorDataItem.ytdChinataiping,
        yearChinataipingCase: inceptedApeByAdvisorDataItem.ytdChinataipingCase,
        monthEtiqa: inceptedApeByAdvisorDataItem.mtdEtiqa,
        monthEtiqaCase: inceptedApeByAdvisorDataItem.mtdEtiqaCase,
        yearEtiqa: inceptedApeByAdvisorDataItem.ytdEtiqa,
        yearEtiqaCase: inceptedApeByAdvisorDataItem.ytdEtiqaCase,
        monthFwd: inceptedApeByAdvisorDataItem.mtdFwd,
        monthFwdCase: inceptedApeByAdvisorDataItem.mtdFwdCase,
        yearFwd: inceptedApeByAdvisorDataItem.ytdFwd,
        yearFwdCase: inceptedApeByAdvisorDataItem.ytdFwdCase,
        monthIncome: inceptedApeByAdvisorDataItem.mtdIncome,
        monthIncomeCase: inceptedApeByAdvisorDataItem.mtdIncomeCase,
        yearIncome: inceptedApeByAdvisorDataItem.ytdIncome,
        yearIncomeCase: inceptedApeByAdvisorDataItem.ytdIncomeCase,
        monthManulife: inceptedApeByAdvisorDataItem.mtdManulife,
        monthManulifeCase: inceptedApeByAdvisorDataItem.mtdManulifeCase,
        yearManulife: inceptedApeByAdvisorDataItem.ytdManulife,
        yearManulifeCase: inceptedApeByAdvisorDataItem.ytdManulifeCase,
        monthRhi: inceptedApeByAdvisorDataItem.mtdRhi,
        monthRhiCase: inceptedApeByAdvisorDataItem.mtdRhiCase,
        yearRhi: inceptedApeByAdvisorDataItem.ytdRhi,
        yearRhiCase: inceptedApeByAdvisorDataItem.ytdRhiCase,
        monthTokiomarine: inceptedApeByAdvisorDataItem.mtdTokiomarine,
        monthTokiomarineCase: inceptedApeByAdvisorDataItem.mtdTokiomarineCase,
        yearTokiomarine: inceptedApeByAdvisorDataItem.ytdTokiomarine,
        yearTokiomarineCase: inceptedApeByAdvisorDataItem.ytdTokiomarineCase,
        monthTransamerica: inceptedApeByAdvisorDataItem.mtdTransamerica,
        monthTransamericaCase: inceptedApeByAdvisorDataItem.mtdTransamericaCase,
        yearTransamerica: inceptedApeByAdvisorDataItem.ytdTransamerica,
        yearTransamericaCase: inceptedApeByAdvisorDataItem.ytdTransamericaCase,
        monthIfast: inceptedApeByAdvisorDataItem.mtdIfast,
        monthIfastCase: inceptedApeByAdvisorDataItem.mtdIfastCase,
        yearIfast: inceptedApeByAdvisorDataItem.ytdIfast,
        yearIfastCase: inceptedApeByAdvisorDataItem.ytdIfastCase,
        monthGrow: inceptedApeByAdvisorDataItem.mtdGrow,
        monthGrowCase: inceptedApeByAdvisorDataItem.mtdGrowCase,
        yearGrow: inceptedApeByAdvisorDataItem.ytdGrow,
        yearGrowCase: inceptedApeByAdvisorDataItem.ytdGrowCase,
        monthTiger: inceptedApeByAdvisorDataItem.mtdTiger,
        monthTigerCase: inceptedApeByAdvisorDataItem.mtdTigerCase,
        yearTiger: inceptedApeByAdvisorDataItem.ytdTiger,
        yearTigerCase: inceptedApeByAdvisorDataItem.ytdTigerCase,
        monthFwdEmber: inceptedApeByAdvisorDataItem.mtdFwdember,
        monthFwdEmberCase: inceptedApeByAdvisorDataItem.mtdFwdemberCase,
        yearFwdEmber: inceptedApeByAdvisorDataItem.ytdFwdember,
        yearFwdEmberCase: inceptedApeByAdvisorDataItem.ytdFwdemberCase,
        monthHsbc: inceptedApeByAdvisorDataItem.mtdHsbc,
        monthHsbcCase: inceptedApeByAdvisorDataItem.mtdHsbcCase,
        yearHsbc: inceptedApeByAdvisorDataItem.ytdHsbc,
        yearHsbcCase: inceptedApeByAdvisorDataItem.ytdHsbcCase,
        yearTarget: inceptedApeByAdvisorDataItem.yealyTarget,
        yearTotal: (inceptedApeByAdvisorDataItem.ytdSinglife + inceptedApeByAdvisorDataItem.ytdNonSinglife),
        userGroup: inceptedApeByAdvisorDataItem.userGroup.toString()
      });
    });

    this.apeCarrierSplitService.applyCarrierSplit(this.inceptedApeByAdvisorData);

    // this.inceptedApeByAdvisorData = this.inceptedApeByAdvisorData.filter(item => item.userGroup !== 'Affinity');
  }
  private __formatSubmissionApeByAdvisorData(submissionApeByAdvisorDataList: any[]) {
    submissionApeByAdvisorDataList.forEach((submissionApeByAdvisorDataItem: any) => {
      this.submissionApeByAdvisorData.push({
        repCode: submissionApeByAdvisorDataItem.piasRepCode,
        personName: submissionApeByAdvisorDataItem.agentRealName.toString().trim(),
        branch: submissionApeByAdvisorDataItem.branch.toString().trim(),
        monthSubmissionSinglife: submissionApeByAdvisorDataItem.mtdSubmissionSinglife,
        monthSubmissionSinglifeCase: submissionApeByAdvisorDataItem.mtdSubmissionSinglifeCase,
        yearSubmissionSinglife: submissionApeByAdvisorDataItem.ytdSubmissionSinglife,
        yearSubmissionSinglifeCase: submissionApeByAdvisorDataItem.ytdSubmissionSinglifeCase,
        yearPendingSinglife: submissionApeByAdvisorDataItem.ytdPendingSinglife,
        yearPendingSinglifeCase: submissionApeByAdvisorDataItem.ytdPendingSinglifeCase,
        yearTarget: submissionApeByAdvisorDataItem.yealyTarget,
        userGroup: submissionApeByAdvisorDataItem.userGroup.toString()
      });
    });

    // this.inceptedApeByAdvisorData = this.inceptedApeByAdvisorData.filter(item => item.userGroup !== 'Affinity');
  }

  private __formatInceptedApeByBranchData(inceptedApeByBranchDataList: any[], affinitybranch: any[]) {
    inceptedApeByBranchDataList.forEach((inceptedApeByBranchDataItem: any) => {
      this.inceptedApeByBranchData.push({
        branchName: inceptedApeByBranchDataItem.branch,
        monthSinglife: inceptedApeByBranchDataItem.mtdSinglife,
        monthSinglifeCase: inceptedApeByBranchDataItem.mtdSinglifeCase,
        monthNonsinglife: inceptedApeByBranchDataItem.mtdNonSinglife,
        monthNonsinglifeCase: inceptedApeByBranchDataItem.mtdNonSinglifeCase,
        yearSinglife: inceptedApeByBranchDataItem.ytdSinglife,
        yearSinglifeCase: inceptedApeByBranchDataItem.ytdSinglifeCase,
        yearNonsinglife: inceptedApeByBranchDataItem.ytdNonSinglife,
        yearNonsinglifeCase: inceptedApeByBranchDataItem.ytdNonSinglifeCase,
        monthChinalife: inceptedApeByBranchDataItem.mtdChinalife,
        monthChinalifeCase: inceptedApeByBranchDataItem.mtdChinalifeCase,
        yearChinalife: inceptedApeByBranchDataItem.ytdChinalife,
        yearChinalifeCase: inceptedApeByBranchDataItem.ytdChinalifeCase,
        monthChinataiping: inceptedApeByBranchDataItem.mtdChinataiping,
        monthChinataipingCase: inceptedApeByBranchDataItem.mtdChinataipingCase,
        yearChinataiping: inceptedApeByBranchDataItem.ytdChinataiping,
        yearChinataipingCase: inceptedApeByBranchDataItem.ytdChinataipingCase,
        monthEtiqa: inceptedApeByBranchDataItem.mtdEtiqa,
        monthEtiqaCase: inceptedApeByBranchDataItem.mtdEtiqaCase,
        yearEtiqa: inceptedApeByBranchDataItem.ytdEtiqa,
        yearEtiqaCase: inceptedApeByBranchDataItem.ytdEtiqaCase,
        monthFwd: inceptedApeByBranchDataItem.mtdFwd,
        monthFwdCase: inceptedApeByBranchDataItem.mtdFwdCase,
        yearFwd: inceptedApeByBranchDataItem.ytdFwd,
        yearFwdCase: inceptedApeByBranchDataItem.ytdFwdCase,
        monthIncome: inceptedApeByBranchDataItem.mtdIncome,
        monthIncomeCase: inceptedApeByBranchDataItem.mtdIncomeCase,
        yearIncome: inceptedApeByBranchDataItem.ytdIncome,
        yearIncomeCase: inceptedApeByBranchDataItem.ytdIncomeCase,
        monthManulife: inceptedApeByBranchDataItem.mtdManulife,
        monthManulifeCase: inceptedApeByBranchDataItem.mtdManulifeCase,
        yearManulife: inceptedApeByBranchDataItem.ytdManulife,
        yearManulifeCase: inceptedApeByBranchDataItem.ytdManulifeCase,
        monthRhi: inceptedApeByBranchDataItem.mtdRhi,
        monthRhiCase: inceptedApeByBranchDataItem.mtdRhiCase,
        yearRhi: inceptedApeByBranchDataItem.ytdRhi,
        yearRhiCase: inceptedApeByBranchDataItem.ytdRhiCase,
        monthTokiomarine: inceptedApeByBranchDataItem.mtdTokiomarine,
        monthTokiomarineCase: inceptedApeByBranchDataItem.mtdTokiomarineCase,
        yearTokiomarine: inceptedApeByBranchDataItem.ytdTokiomarine,
        yearTokiomarineCase: inceptedApeByBranchDataItem.ytdTokiomarineCase,
        monthTransamerica: inceptedApeByBranchDataItem.mtdTransamerica,
        monthTransamericaCase: inceptedApeByBranchDataItem.mtdTransamericaCase,
        yearTransamerica: inceptedApeByBranchDataItem.ytdTransamerica,
        yearTransamericaCase: inceptedApeByBranchDataItem.ytdTransamericaCase,
        monthIfast: inceptedApeByBranchDataItem.mtdIfast,
        monthIfastCase: inceptedApeByBranchDataItem.mtdIfastCase,
        yearIfast: inceptedApeByBranchDataItem.ytdIfast,
        yearIfastCase: inceptedApeByBranchDataItem.ytdIfastCase,
        monthGrow: inceptedApeByBranchDataItem.mtdGrow,
        monthGrowCase: inceptedApeByBranchDataItem.mtdGrowCase,
        yearGrow: inceptedApeByBranchDataItem.ytdGrow,
        yearGrowCase: inceptedApeByBranchDataItem.ytdGrowCase,
        monthTiger: inceptedApeByBranchDataItem.mtdTiger,
        monthTigerCase: inceptedApeByBranchDataItem.mtdTigerCase,
        yearTiger: inceptedApeByBranchDataItem.ytdTiger,
        yearTigerCase: inceptedApeByBranchDataItem.ytdTigerCase,
        monthFwdEmber: inceptedApeByBranchDataItem.mtdFwdember,
        monthFwdEmberCase: inceptedApeByBranchDataItem.mtdFwdemberCase,
        yearFwdEmber: inceptedApeByBranchDataItem.ytdFwdember,
        yearFwdEmberCase: inceptedApeByBranchDataItem.ytdFwdemberCase,
        monthHsbc: inceptedApeByBranchDataItem.mtdHsbc,
        monthHsbcCase: inceptedApeByBranchDataItem.mtdHsbcCase,
        yearHsbc: inceptedApeByBranchDataItem.ytdHsbc,
        yearHsbcCase: inceptedApeByBranchDataItem.ytdHsbcCase,
        yearTarget: inceptedApeByBranchDataItem.yealyTarget,
        yearTotal: (inceptedApeByBranchDataItem.ytdSinglife + inceptedApeByBranchDataItem.ytdNonSinglife)
      });
    });

    this.apeCarrierSplitService.applyCarrierSplit(this.inceptedApeByBranchData);

    // this.inceptedApeByBranchData = this.inceptedApeByBranchData.filter(branch => !affinitybranch.find(item => item.branchName === branch.branchName));
  }

  private __formatSubmissionApeByBranchData(submissionApeByBranchDataList: any[], affinitybranch: any[]) {
    submissionApeByBranchDataList.forEach((submissionApeByBranchDataItem: any) => {
      this.submissionApeByBranchData.push({
        branchName: submissionApeByBranchDataItem.branch,
        monthSubmissionSinglife: submissionApeByBranchDataItem.mtdSubmissionSinglife,
        monthSubmissionSinglifeCase: submissionApeByBranchDataItem.mtdSubmissionSinglifeCase,
        yearSubmissionSinglife: submissionApeByBranchDataItem.ytdSubmissionSinglife,
        yearSubmissionSinglifeCase: submissionApeByBranchDataItem.ytdSubmissionSinglifeCase,
        yearPendingSinglife: submissionApeByBranchDataItem.ytdPendingSinglife,
        yearPendingSinglifeCase: submissionApeByBranchDataItem.ytdPendingSinglifeCase,
        yearTarget: submissionApeByBranchDataItem.yealyTarget
      });
    });

    // this.inceptedApeByBranchData = this.inceptedApeByBranchData.filter(branch => !affinitybranch.find(item => item.branchName === branch.branchName));
  }

  protected __applyInceptedApeByAdvisorDataFilter() {
    let filterValue: string = this.inceptedApeByAdvisorTableFilterFc.value?.toString() || '';

    let tempInceptedApeByAdvisorTableData: InceptedApeByAdvisorsV2[] = [];

    if (filterValue) {
      tempInceptedApeByAdvisorTableData = this.inceptedApeByAdvisorData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempInceptedApeByAdvisorTableData = this.inceptedApeByAdvisorData;
    }

    this.inceptedApeByAdvisorTableDataSource.data = tempInceptedApeByAdvisorTableData;
  }

  protected __applySubmissionApeByAdvisorDataFilter() {
    let filterValue: string = this.submissionApeByAdvisorTableFilterFc.value?.toString() || '';

    let tempSubmissionApeByAdvisorTableData: SubmissionApeByAdvisorsV2[] = [];

    if (filterValue) {
      tempSubmissionApeByAdvisorTableData = this.submissionApeByAdvisorData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempSubmissionApeByAdvisorTableData = this.submissionApeByAdvisorData;
    }

    this.submissionApeByAdvisorTableDataSource.data = tempSubmissionApeByAdvisorTableData;
  }

  protected __applyInceptedApeByBranchDataFilter() {
    let filterValue: string = this.inceptedApeByBranchTableFilterFc.value?.toString() || '';

    let tempInceptedApeByBranchTableData: InceptedApeByBranchesV2[] = [];

    if (filterValue) {
      tempInceptedApeByBranchTableData = this.inceptedApeByBranchData.filter(apeData =>
        apeData.branchName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempInceptedApeByBranchTableData = this.inceptedApeByBranchData;
    }

    this.inceptedApeByBranchTableDataSource.data = tempInceptedApeByBranchTableData;
  }

  protected __applySubmissionApeByBranchDataFilter() {
    let filterValue: string = this.submissionApeByBranchTableFilterFc.value?.toString() || '';

    let tempSubmissionApeByBranchTableData: SubmissionApeByBranchesV2[] = [];

    if (filterValue) {
      tempSubmissionApeByBranchTableData = this.submissionApeByBranchData.filter(apeData =>
        apeData.branchName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempSubmissionApeByBranchTableData = this.submissionApeByBranchData;
    }

    this.submissionApeByBranchTableDataSource.data = tempSubmissionApeByBranchTableData;
  }

  protected __applyInceptedApeByManagerDataFilter() {
    let filterValue: string = this.inceptedApeByManagerTableFilterFc.value?.toString() || '';

    let tempInceptedApeByManagerTableData: InceptedApeByAdvisorsV2[] = [];

    if (filterValue) {
      tempInceptedApeByManagerTableData = this.inceptedApeByManagerData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempInceptedApeByManagerTableData = this.inceptedApeByManagerData;
    }

    this.inceptedApeByManagerTableDataSource.data = tempInceptedApeByManagerTableData;
  }

  protected __applySubmissionApeByManagerDataFilter() {
    let filterValue: string = this.submissionApeByManagerTableFilterFc.value?.toString() || '';

    let tempSubmissionApeByManagerTableData: SubmissionApeByAdvisorsV2[] = [];

    if (filterValue) {
      tempSubmissionApeByManagerTableData = this.submissionApeByManagerData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempSubmissionApeByManagerTableData = this.submissionApeByManagerData;
    }

    this.submissionApeByManagerTableDataSource.data = tempSubmissionApeByManagerTableData;
  }

  protected __applyIndividualInceptedApeByBranchDataFilter() {
    let filterValue: string = this.individualInceptedApeByBranchTableFilterFc.value?.toString() || '';

    let tempIndividualInceptedApeByBranchTableData: InceptedApeByAdvisorsV2[] = [];

    if (filterValue) {
      // Branch is a visible column here, so it joins rep code and name as a search key.
      tempIndividualInceptedApeByBranchTableData = this.individualInceptedApeByBranchData.filter(apeData =>
        apeData.repCode.toString().includes(filterValue)
        || apeData.personName.toLowerCase().includes(filterValue.toLowerCase())
        || apeData.branch.toLowerCase().includes(filterValue.toLowerCase()
        ));
    } else {
      tempIndividualInceptedApeByBranchTableData = this.individualInceptedApeByBranchData;
    }

    this.individualInceptedApeByBranchTableDataSource.data = tempIndividualInceptedApeByBranchTableData;
  }

  protected __displayInceptedApeBranchWiseAdvisors(brancName: string) {
    const viewInceptedApeBranchAdvisorsDialogRef = this.matDialog.open(ViewInceptedApeBranchAdvisorsDialogV2, {
      data: {
        branchName: brancName,
        targetYear: this.targetYear
      },
      width: "calc(100vw - 30px)",
      maxWidth: "calc(100vw - 30px)",
      height: "calc(100vh - 75px)",
      panelClass: 'branch-popup-dialog',
      maxHeight: "calc(100vh - 75px)",
      position: {
        bottom: "20px"
      }
    });
  }

  protected __displaySubmissionApeBranchWiseAdvisors(brancName: string) {
    const viewSubmissionApeBranchAdvisorsDialogRef = this.matDialog.open(ViewSubmissionApeBranchAdvisorsDialogV2, {
      data: {
        branchName: brancName,
        targetYear: this.targetYear
      },
      width: "calc(100vw - 30px)",
      maxWidth: "calc(100vw - 30px)",
      height: "calc(100vh - 75px)",
      panelClass: 'branch-popup-dialog',
      maxHeight: "calc(100vh - 75px)",
      position: {
        bottom: "20px"
      }
    });
  }

  protected __displayInceptedManagerWiseAdvisors(managerRepCode: string, managerName: string) {
    const viewManagerAdvisorsDialogRef = this.matDialog.open(ViewInceptedApeManagerAdvisorsDialogV2, {
      data: {
        managerRepCode: managerRepCode,
        managerName: managerName,
        targetYear: this.targetYear
      },
      width: "calc(100vw - 30px)",
      maxWidth: "calc(100vw - 30px)",
      height: "calc(100vh - 75px)",
      panelClass: 'branch-popup-dialog',
      maxHeight: "calc(100vh - 75px)",
      position: {
        bottom: "20px"
      }
    });
  }

  protected __displaySubmissionManagerWiseAdvisors(managerRepCode: string, managerName: string) {
    const viewManagerAdvisorsDialogRef = this.matDialog.open(ViewSubmissionApeManagerAdvisorsDialogV2, {
      data: {
        managerRepCode: managerRepCode,
        managerName: managerName,
        targetYear: this.targetYear
      },
      width: "calc(100vw - 30px)",
      maxWidth: "calc(100vw - 30px)",
      height: "calc(100vh - 75px)",
      panelClass: 'branch-popup-dialog',
      maxHeight: "calc(100vh - 75px)",
      position: {
        bottom: "20px"
      }
    });
  }

}
